import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import openHtmlInNewTab from '@/lib/openHtmlInNewTab';
// Phase 185.e — popup-blocker-proof signed view links
import openSignedDocInNewTab from '@/lib/openSignedDocInNewTab';
import { downloadDocument } from '@/lib/downloadDocument';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  Receipt, Plus, Sparkles, Search, CheckCircle, Send, MoreHorizontal,
  Loader2, Eye, Download, CreditCard, AlertTriangle, Shield, Trash2,
  Merge, ClipboardCheck, Scan, Zap, XCircle, ArrowRight, IndianRupee,
  TrendingUp, Clock, FileText, Edit, MessageSquare, BarChart3, Lightbulb, Ban,
  RefreshCw, Activity, Bell,
} from 'lucide-react';
import { toast } from 'sonner';

const statusColors = {
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  paid: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  overdue: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  cancelled: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
};

const matchTypeConfig = {
  exact: { label: 'Exact', color: 'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-400', icon: '🔴' },
  fuzzy: { label: 'Fuzzy', color: 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-400', icon: '🟠' },
  near: { label: 'Near', color: 'bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-400', icon: '🟡' },
};

// KPI Filter Card
function KPICard({ icon: Icon, title, count, amount, isActive, onClick, iconBg, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`group relative w-full text-left rounded-xl border-2 p-4 transition-all duration-200 overflow-hidden
        ${isActive ? 'border-primary shadow-lg shadow-primary/10 scale-[1.02]' : 'border-transparent hover:border-border hover:shadow-md hover:-translate-y-0.5'}`}
    >
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-2">
          <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${iconBg}`}>
            <Icon className="h-4 w-4 text-white" />
          </div>
          {isActive && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-primary text-primary-foreground">Filtered</span>}
        </div>
        <p className="text-xs font-medium text-muted-foreground">{title}</p>
        <p className="text-2xl font-display font-bold tabular-nums mt-0.5">{count}</p>
        {amount !== undefined && <p className="text-xs text-muted-foreground tabular-nums">₹{amount.toLocaleString()}</p>}
      </div>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Phase 637 — Executive Monthly Billing Cockpit
// One-glance health: MRR this month · sweep status · ageing buckets ·
// at-risk academies · 6-month MRR trend sparkline
// ─────────────────────────────────────────────────────────────────────

function relTime(iso) {
  if (!iso) return 'never';
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return `${Math.round(diff)}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    return `${Math.round(diff / 86400)}d ago`;
  } catch { return 'never'; }
}

function MrrSparkline({ trend }) {
  if (!trend || trend.length === 0) return null;
  const max = Math.max(...trend.map(t => t.mrr || 0), 1);
  return (
    <div className="flex items-end gap-1 h-10">
      {trend.slice(-6).map((t, i) => {
        const h = Math.max(6, Math.round((t.mrr / max) * 40));
        return (
          <div key={t.month} className="flex flex-col items-center gap-0.5">
            <div className="w-4 rounded-sm bg-gradient-to-t from-emerald-500 to-emerald-300"
                 style={{ height: `${h}px` }}
                 title={`${t.month}: ₹${(t.mrr || 0).toLocaleString()} · ${t.count} inv`} />
            <span className="text-[8px] font-mono text-slate-400">{t.month.slice(5)}</span>
          </div>
        );
      })}
    </div>
  );
}

function BillingCockpit() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(null); // 'billing' | 'reminder' | null

  const fetchCockpit = useCallback(async () => {
    try {
      const r = await api.get('/super-admin/billing/cockpit');
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load billing cockpit');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCockpit(); }, [fetchCockpit]);

  const runSweep = useCallback(async (kind) => {
    const label = kind === 'billing'
      ? 'monthly-billing'
      : kind === 'reminder' ? 'reminder' : 'auto-throttle';
    const path = kind === 'billing'
      ? '/super-admin/billing/monthly-sweep-now'
      : kind === 'reminder'
        ? '/super-admin/billing/reminder-sweep-now'
        : '/super-admin/billing/auto-throttle-now';
    if (!window.confirm(`Fire the ${label} sweep NOW? It normally runs once every 24h automatically.`)) return;
    setTriggering(kind);
    try {
      const r = await api.post(path);
      toast.success(r.data.message || `${label} sweep dispatched`, { duration: 6000 });
      setTimeout(fetchCockpit, 8000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `${label} sweep failed`);
    } finally {
      setTriggering(null);
    }
  }, [fetchCockpit]);

  if (loading) return <Skeleton className="h-40 rounded-xl" />;
  if (!data) return null;

  const {
    mrr_this_month: mrrMonth = 0,
    invoices_this_month: mInv = {},
    mrr_trend: trend = [],
    ageing = { counts: {}, amounts: {} },
    at_risk_academies: atRisk = [],
    at_risk_total: atRiskTotal = 0,
    throttled_count: throttledCount = 0,
    last_billing_run: lastBill,
    last_reminder_run: lastRem,
    last_throttle_run: lastThr,
  } = data;

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-gradient-to-br from-slate-50 to-white dark:from-slate-950 dark:to-slate-900 p-4"
         data-testid="billing-cockpit">
      {/* Header row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-emerald-600" />
          <h2 className="text-sm font-semibold">Monthly Billing Cockpit</h2>
          <span className="text-[10px] font-mono text-slate-400">autonomous · updates every 24h</span>
        </div>
        <Button size="sm" variant="ghost" onClick={fetchCockpit}
                className="h-7 text-xs" data-testid="cockpit-refresh-btn">
          <RefreshCw className="h-3 w-3 mr-1" /> Refresh
        </Button>
      </div>

      {/* Row 1 — MRR, this-month, sweep tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {/* MRR tile */}
        <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 p-3"
             data-testid="cockpit-mrr">
          <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">MRR this month</p>
          <p className="text-2xl font-display font-bold tabular-nums mt-0.5">
            ₹{mrrMonth.toLocaleString(undefined, {maximumFractionDigits:0})}
          </p>
          <MrrSparkline trend={trend} />
        </div>

        {/* This-month invoices */}
        <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 p-3"
             data-testid="cockpit-this-month">
          <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">This month</p>
          <p className="text-2xl font-display font-bold tabular-nums mt-0.5">{mInv.generated || 0}</p>
          <div className="flex gap-2 mt-1 text-xs">
            <span className="text-emerald-600">{mInv.paid || 0} paid</span>
            <span className="text-amber-600">{mInv.pending || 0} pending</span>
          </div>
        </div>

        {/* Last billing sweep */}
        <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 p-3"
             data-testid="cockpit-last-billing">
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Auto-billing</p>
            <Button size="sm" variant="outline" className="h-6 text-[10px] px-2"
                    onClick={() => runSweep('billing')} disabled={triggering === 'billing'}
                    data-testid="cockpit-billing-run-btn">
              {triggering === 'billing' ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Run now'}
            </Button>
          </div>
          <p className="text-lg font-display font-bold tabular-nums mt-0.5">
            {lastBill?.generated ?? 0}<span className="text-xs font-normal text-slate-400"> generated</span>
          </p>
          <p className="text-[10px] text-slate-500">last ran {relTime(lastBill?.ran_at)}</p>
        </div>

        {/* Last reminder sweep */}
        <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 p-3"
             data-testid="cockpit-last-reminder">
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Auto-reminders</p>
            <Button size="sm" variant="outline" className="h-6 text-[10px] px-2"
                    onClick={() => runSweep('reminder')} disabled={triggering === 'reminder'}
                    data-testid="cockpit-reminder-run-btn">
              {triggering === 'reminder' ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Run now'}
            </Button>
          </div>
          <p className="text-lg font-display font-bold tabular-nums mt-0.5">
            {((lastRem?.soft_sent || 0) + (lastRem?.firm_sent || 0) + (lastRem?.final_sent || 0))}
            <span className="text-xs font-normal text-slate-400"> sent</span>
          </p>
          <p className="text-[10px] text-slate-500">last ran {relTime(lastRem?.ran_at)}</p>
        </div>

        {/* Phase 638 — Auto-Throttle tile */}
        <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 p-3"
             data-testid="cockpit-last-throttle">
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Auto-throttle</p>
            <Button size="sm" variant="outline" className="h-6 text-[10px] px-2"
                    onClick={() => runSweep('throttle')} disabled={triggering === 'throttle'}
                    data-testid="cockpit-throttle-run-btn">
              {triggering === 'throttle' ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Run now'}
            </Button>
          </div>
          <div className="flex items-baseline gap-2 mt-0.5">
            <p className="text-lg font-display font-bold tabular-nums text-red-600">
              {throttledCount}
            </p>
            <span className="text-xs text-slate-500">suspended</span>
            <span className="text-xs text-slate-400 tabular-nums">
              {lastThr ? `+${lastThr.suspended || 0} today` : ''}
            </span>
          </div>
          <p className="text-[10px] text-slate-500">last ran {relTime(lastThr?.ran_at)}</p>
        </div>
      </div>

      {/* Row 2 — Ageing buckets */}
      <div className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-2" data-testid="cockpit-ageing">
        <AgeingBucket label="< 1 day" count={ageing.counts?.lt_1d || 0} amount={ageing.amounts?.lt_1d || 0} tone="emerald" />
        <AgeingBucket label="1–3 days" count={ageing.counts?.['1_3d'] || 0} amount={ageing.amounts?.['1_3d'] || 0} tone="amber" />
        <AgeingBucket label="3–5 days" count={ageing.counts?.['3_5d'] || 0} amount={ageing.amounts?.['3_5d'] || 0} tone="orange" />
        <AgeingBucket label="5+ days" count={ageing.counts?.gt_5d || 0} amount={ageing.amounts?.gt_5d || 0} tone="red" />
      </div>

      {/* Row 3 — At-risk academies */}
      {atRiskTotal > 0 && (
        <div className="mt-3 rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50/60 dark:bg-red-950/20 p-3"
             data-testid="cockpit-at-risk">
          <div className="flex items-center gap-2 mb-1.5">
            <Bell className="h-3.5 w-3.5 text-red-600" />
            <span className="text-xs font-semibold text-red-700 dark:text-red-400">
              {atRiskTotal} at-risk {atRiskTotal === 1 ? 'academy' : 'academies'} — 2+ overdue invoices
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {atRisk.map(a => (
              <span key={a.academy_id}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white dark:bg-slate-900 border border-red-200 text-[11px]">
                <span className="font-medium">{a.academy_name}</span>
                <span className="text-red-600 font-mono tabular-nums">×{a.pending_count}</span>
                <span className="text-slate-500 tabular-nums">₹{a.total_amount.toLocaleString(undefined, {maximumFractionDigits:0})}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AgeingBucket({ label, count, amount, tone }) {
  const tones = {
    emerald: 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-900',
    amber:   'bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-900',
    orange:  'bg-orange-50 dark:bg-orange-950/30 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-900',
    red:     'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-900',
  };
  return (
    <div className={`rounded-lg border p-2.5 ${tones[tone] || tones.emerald}`}
         data-testid={`cockpit-ageing-${tone}`}>
      <p className="text-[10px] uppercase tracking-wide font-semibold opacity-80">{label}</p>
      <div className="flex items-baseline justify-between gap-1 mt-0.5">
        <p className="text-lg font-display font-bold tabular-nums">{count}</p>
        <p className="text-[10px] tabular-nums opacity-70">₹{amount.toLocaleString(undefined, {maximumFractionDigits:0})}</p>
      </div>
    </div>
  );
}

export default function AdminInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [academies, setAcademies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [academyFilter, setAcademyFilter] = useState('all'); // Phase 68 — filter by academy
  const [selectedIds, setSelectedIds] = useState([]);

  // Dialogs
  const [showCreate, setShowCreate] = useState(false);
  const [showMarkPaid, setShowMarkPaid] = useState(null);
  const [showEdit, setShowEdit] = useState(null);
  const [editForm, setEditForm] = useState({ amount: '', due_date: '', notes: '' });
  const [payForm, setPayForm] = useState({ amount: '', payment_date: new Date().toISOString().slice(0,10), payment_mode: 'UPI', reference_id: '' });
  const [form, setForm] = useState({ academy_id: '', plan: '', amount: '', due_date: '', description: '', notes: '', notify: true });
  const [generating, setGenerating] = useState(false);

  // AI State
  const [duplicateData, setDuplicateData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [reminding, setReminding] = useState(false);
  const [activeTab, setActiveTab] = useState('invoices');
  const [downloadingIds, setDownloadingIds] = useState(new Set());

  // Phase 254 — Reconciliation Audit Log state.
  const [recoveryData, setRecoveryData] = useState(null);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [sweepRunning, setSweepRunning] = useState(false);

  const fetchRecoveryAudit = useCallback(async () => {
    setRecoveryLoading(true);
    try {
      const r = await api.get('/super-admin/reconciliation-logs', { params: { days: 30 } });
      setRecoveryData(r?.data || null);
    } catch (e) {
      // Surface upstream error; don't crash the panel.
    } finally {
      setRecoveryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'recovery') fetchRecoveryAudit();
  }, [activeTab, fetchRecoveryAudit]);

  const handleRunSweepNow = async () => {
    setSweepRunning(true);
    try {
      const r = await api.post('/super-admin/subscription-invoices/nightly-reconcile-sweep');
      const c = r?.data || {};
      toast.success(`Sweep complete · ${c.reconciled || 0} reconciled · ${c.not_captured || 0} not captured · ${c.errors || 0} errors`);
      fetchRecoveryAudit();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Sweep failed';
      toast.error(detail);
    } finally {
      setSweepRunning(false);
    }
  };

  // Fetch core data
  const fetchData = useCallback(async () => {
    try {
      const [invRes, statsRes, acadRes] = await Promise.all([
        api.get('/super-admin/invoices', { params: { search: search || undefined } }),
        api.get('/super-admin/invoices/stats'),
        api.get('/admin/academies')
      ]);
      setInvoices(invRes.data); setStats(statsRes.data); setAcademies(acadRes.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-scan + insights on load
  useEffect(() => {
    if (!loading && invoices.length > 0) {
      handleScanDuplicates(true);
      fetchInsights(true);
    }
  }, [loading]);

  // Compute KPI stats
  const kpiStats = useMemo(() => {
    const all = invoices.filter(i => i.status !== 'cancelled');
    const pending = all.filter(i => i.status === 'pending');
    const paid = all.filter(i => i.status === 'paid');
    const overdue = all.filter(i => i.status === 'overdue');
    return {
      total: all.length, totalAmt: all.reduce((s, i) => s + (i.amount || 0), 0),
      pending: pending.length, pendingAmt: pending.reduce((s, i) => s + (i.amount || 0), 0),
      paid: paid.length, paidAmt: paid.reduce((s, i) => s + (i.amount || 0), 0),
      overdue: overdue.length, overdueAmt: overdue.reduce((s, i) => s + (i.amount || 0), 0),
    };
  }, [invoices]);

  // Build duplicate lookup
  const duplicateLookup = useMemo(() => {
    const map = {};
    if (!duplicateData) return map;
    for (const dup of duplicateData.duplicates) {
      const cfgA = matchTypeConfig[dup.match_type] || matchTypeConfig.near;
      const cfgB = cfgA;
      if (!map[dup.invoice_a.id]) map[dup.invoice_a.id] = { type: dup.match_type, icon: cfgA.icon, conf: dup.confidence };
      if (!map[dup.invoice_b.id]) map[dup.invoice_b.id] = { type: dup.match_type, icon: cfgB.icon, conf: dup.confidence };
    }
    return map;
  }, [duplicateData]);

  // Filtered invoices
  const filtered = useMemo(() => {
    let list = invoices;
    if (activeFilter === 'pending') list = list.filter(i => i.status === 'pending');
    else if (activeFilter === 'paid') list = list.filter(i => i.status === 'paid');
    else if (activeFilter === 'overdue') list = list.filter(i => i.status === 'overdue');
    else list = list.filter(i => i.status !== 'cancelled');
    // Phase 68 — academy filter (server already supports academy_id but we filter client-side for snappy UX)
    if (academyFilter && academyFilter !== 'all') {
      list = list.filter(i => i.academy_id === academyFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(i => i.custom_id?.toLowerCase().includes(q) || i.academy_name?.toLowerCase().includes(q) || i.academy_custom_id?.toLowerCase().includes(q));
    }
    return list;
  }, [invoices, activeFilter, academyFilter, search]);

  // ========== AI Actions ==========
  const handleScanDuplicates = async (silent = false) => {
    if (!silent) setScanning(true);
    try { const res = await api.get('/super-admin/invoices/detect-duplicates'); setDuplicateData(res.data); }
    catch (err) { console.error(err); }
    finally { if (!silent) setScanning(false); }
  };

  const fetchInsights = async (silent = false) => {
    try { const res = await api.post('/super-admin/invoices/ai-insights'); setInsights(res.data); }
    catch (err) { console.error(err); }
  };

  const handleCleanAll = async () => {
    setCleaning(true);
    try {
      const res = await api.post('/super-admin/invoices/clean-all-duplicates');
      toast.success(res.data.message);
      fetchData(); setTimeout(() => { handleScanDuplicates(true); fetchInsights(true); }, 500);
    } catch (err) { toast.error('Cleanup failed'); }
    finally { setCleaning(false); }
  };

  const handleResolveDuplicate = async (dup, action) => {
    try {
      await api.post('/super-admin/invoices/resolve-duplicate', {
        action, invoice_a_id: dup.invoice_a.id, invoice_b_id: dup.invoice_b.id,
        keep_id: dup.invoice_a.id, delete_id: dup.invoice_b.id,
      });
      toast.success(`Duplicate ${action}d`);
      fetchData(); setTimeout(() => { handleScanDuplicates(true); fetchInsights(true); }, 500);
    } catch (err) { toast.error('Failed to resolve'); }
  };

  // ========== Invoice Actions ==========
  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      const payload = { academy_id: form.academy_id, notify: form.notify };
      if (form.plan) payload.plan = form.plan;
      if (form.amount && parseFloat(form.amount) > 0) payload.amount = parseFloat(form.amount);
      if (form.due_date) payload.due_date = form.due_date;
      if (form.description) payload.description = form.description;
      if (form.notes) payload.notes = form.notes;
      const r = await api.post('/super-admin/invoices/generate', payload);
      toast.success(`Invoice ${r.data?.custom_id || ''} generated · ₹${r.data?.amount?.toLocaleString?.() || ''}` + (r.data?.notify_queued ? ' · WhatsApp & Email queued' : ''));
      setShowCreate(false);
      setForm({ academy_id: '', plan: '', amount: '', due_date: '', description: '', notes: '', notify: true });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setGenerating(false);
    }
  };

  const handleAutoGenerate = async () => {
    setGenerating(true);
    try { const r = await api.post('/super-admin/invoices/auto-generate'); toast.success(r.data.message); fetchData(); }
    catch (err) { toast.error('Failed'); } finally { setGenerating(false); }
  };

  const handleMarkPaid = async () => {
    if (!showMarkPaid || !payForm.amount || !payForm.reference_id) { toast.error('Fill all payment details'); return; }
    try {
      await api.put(`/super-admin/invoices/${showMarkPaid.id}/mark-paid`);
      toast.success(`${showMarkPaid.custom_id} marked as paid!`);
      setShowMarkPaid(null); fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  const handleEdit = async () => {
    if (!showEdit) return;
    try {
      await api.put(`/super-admin/invoices/${showEdit.id}/edit`, { amount: parseFloat(editForm.amount) || showEdit.amount, due_date: editForm.due_date || showEdit.due_date, notes: editForm.notes });
      toast.success('Invoice updated'); setShowEdit(null); fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  const handleDelete = async (inv) => {
    if (!window.confirm(`Delete ${inv.custom_id}? This cannot be undone.`)) return;
    try { await api.delete(`/super-admin/invoices/${inv.id}`); toast.success('Invoice deleted'); fetchData(); }
    catch (err) { toast.error('Failed'); }
  };

  // Phase 254 — pull-based Razorpay self-heal. Hits the new
  // POST /super-admin/subscription-invoices/{id}/reconcile-razorpay endpoint,
  // which queries Razorpay's Orders API for `status=captured` payments and
  // flips the invoice to paid + reactivates the academy + cross-posts the
  // SaaS expense. Idempotent server-side; safe to click multiple times.
  const [reconcilingIds, setReconcilingIds] = useState(new Set());
  const handleReconcileRazorpay = async (inv) => {
    setReconcilingIds((prev) => { const n = new Set(prev); n.add(inv.id); return n; });
    try {
      const r = await api.post(`/super-admin/subscription-invoices/${inv.id}/reconcile-razorpay`);
      const data = r?.data || {};
      if (data.status === 'reconciled') {
        toast.success(`${inv.custom_id} self-healed via Razorpay · payment ${data.razorpay_payment_id?.slice(0, 14)}…`);
        fetchData();
      } else if (data.status === 'already_paid') {
        toast.success(`${inv.custom_id} is already paid (refreshing grid).`);
        fetchData();
      } else if (data.status === 'not_captured') {
        toast.warning(`Razorpay has no captured payment for ${inv.custom_id} yet. Statuses seen: ${(data.razorpay_statuses || []).join(', ') || 'none'}.`);
      } else {
        toast.message(data.message || `Reconcile returned: ${data.status}`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Reconcile failed';
      toast.error(detail);
    } finally {
      setReconcilingIds((prev) => { const n = new Set(prev); n.delete(inv.id); return n; });
    }
  };

  const handleViewInvoice = (inv) => openSignedDocInNewTab(
    `/invoice/${inv.id}/pdf`,
    { label: 'Invoice', errorMessage: 'Failed to load invoice' },
  );

  const handleDownloadInvoice = async (inv) => {
    const invId = inv.id;
    setDownloadingIds(prev => new Set(prev).add(invId));
    try {
      await downloadDocument(
        api,
        `/subscription-invoices/${inv.id}/download`,
        `TaxInvoice_${inv.custom_id || 'INVOICE'}.pdf`,
        { label: `Tax Invoice ${inv.custom_id || ''}` }
      );
    } finally {
      setDownloadingIds(prev => { const next = new Set(prev); next.delete(invId); return next; });
    }
  };

  // Phase 68 — Digital receipt download (paid invoices only)
  const handleDownloadReceipt = async (inv) => {
    const invId = inv.id;
    setDownloadingIds(prev => new Set(prev).add(invId));
    try {
      await downloadDocument(
        api,
        `/subscription-invoices/${inv.id}/receipt/download`,
        `Receipt_${inv.custom_id || 'INVOICE'}.pdf`,
        { label: `Receipt ${inv.custom_id || ''}` }
      );
    } finally {
      setDownloadingIds(prev => { const next = new Set(prev); next.delete(invId); return next; });
    }
  };

  // Phase 68 — Send a single reminder
  const handleSendReminder = async (inv) => {
    try {
      await api.post(`/super-admin/invoices/${inv.id}/send-reminder`);
      toast.success(`Reminder dispatched for ${inv.custom_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to dispatch reminder');
    }
  };

  const handleBulkRemind = async () => {
    setReminding(true);
    try {
      const ids = selectedIds.length > 0 ? selectedIds : [];
      const res = await api.post('/super-admin/invoices/bulk-remind', { invoice_ids: ids });
      toast.success(res.data.message);
      setSelectedIds([]); fetchData();
    } catch (err) { toast.error('Failed'); }
    finally { setReminding(false); }
  };

  const handleBulkMarkPaid = async () => {
    for (const id of selectedIds) {
      try { await api.put(`/super-admin/invoices/${id}/mark-paid`); } catch (err) { /* skip */ }
    }
    toast.success(`${selectedIds.length} invoices marked as paid`);
    setSelectedIds([]); fetchData();
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleSelectAll = () => {
    const selectable = filtered.filter(i => i.status !== 'cancelled' && i.status !== 'paid');
    if (selectedIds.length === selectable.length) setSelectedIds([]);
    else setSelectedIds(selectable.map(i => i.id));
  };

  const summary = duplicateData?.summary;
  const duplicates = duplicateData?.duplicates || [];
  const hasDuplicates = summary && summary.duplicates_found > 0;
  const recs = insights?.recommendations || [];

  if (loading) return (
    <div className="space-y-4" data-testid="invoices-loading">
      <Skeleton className="h-8 w-48" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
      <Skeleton className="h-64 rounded-xl" />
    </div>
  );

  return (
    <div className="space-y-5" data-testid="admin-invoices-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-semibold">Subscription Invoices</h1>
          <p className="text-sm text-muted-foreground">AI-powered billing & duplicate detection</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => handleScanDuplicates(false)} disabled={scanning} data-testid="scan-duplicates-button">
            {scanning ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Scan className="h-4 w-4 mr-1" />} AI Scan
          </Button>
          <Button size="sm" onClick={handleAutoGenerate} disabled={generating}>
            {generating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />} Auto-Generate
          </Button>
        </div>
      </div>

      {/* 4 KPI Filter Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="kpi-filter-cards">
        <KPICard icon={Receipt} title="Total Invoices" count={kpiStats.total} amount={kpiStats.totalAmt}
          isActive={activeFilter === 'all'} onClick={() => setActiveFilter('all')} iconBg="bg-slate-600" testId="kpi-total" />
        <KPICard icon={AlertTriangle} title="Overdue" count={kpiStats.overdue} amount={kpiStats.overdueAmt}
          isActive={activeFilter === 'overdue'} onClick={() => setActiveFilter(activeFilter === 'overdue' ? 'all' : 'overdue')} iconBg="bg-red-600" testId="kpi-overdue" />
        <KPICard icon={Clock} title="Pending" count={kpiStats.pending} amount={kpiStats.pendingAmt}
          isActive={activeFilter === 'pending'} onClick={() => setActiveFilter(activeFilter === 'pending' ? 'all' : 'pending')} iconBg="bg-amber-600" testId="kpi-pending" />
        <KPICard icon={CheckCircle} title="Paid" count={kpiStats.paid} amount={kpiStats.paidAmt}
          isActive={activeFilter === 'paid'} onClick={() => setActiveFilter(activeFilter === 'paid' ? 'all' : 'paid')} iconBg="bg-emerald-600" testId="kpi-paid" />
      </div>

      {/* Phase 637 — Executive Monthly Billing Cockpit */}
      <BillingCockpit />

      {/* AI Duplicate Alert Banner */}
      {hasDuplicates && (
        <Alert className="border-red-300 bg-red-50 dark:bg-red-950/20 dark:border-red-800" data-testid="duplicate-alert-banner">
          <AlertTriangle className="h-5 w-5 text-red-600" />
          <AlertTitle className="text-red-700 dark:text-red-400 font-display">
            AI Detected: {summary.duplicates_found} Duplicate{summary.duplicates_found > 1 ? 's' : ''} (₹{summary.total_risk.toLocaleString()} Risk)
          </AlertTitle>
          <AlertDescription>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-1">
              <div className="flex items-center gap-3 text-xs text-red-600 dark:text-red-400">
                <span><span className="inline-block h-2 w-2 rounded-full bg-red-500" /> {summary.exact_matches} Exact</span>
                <span><span className="inline-block h-2 w-2 rounded-full bg-amber-500" /> {summary.fuzzy_matches} Fuzzy</span>
                <span><span className="inline-block h-2 w-2 rounded-full bg-yellow-500" /> {summary.near_matches} Near</span>
                <span className="text-muted-foreground">{summary.accuracy}% acc | {summary.processing_time_seconds}s</span>
              </div>
              <div className="flex items-center gap-1.5 sm:ml-auto">
                <Button size="sm" variant="destructive" onClick={handleCleanAll} disabled={cleaning} data-testid="clean-all-button" className="h-7 text-xs">
                  {cleaning ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Zap className="h-3 w-3 mr-1" />} Clean All
                </Button>
                <Button size="sm" variant="outline" onClick={() => setActiveTab('duplicates')} className="h-7 text-xs" data-testid="review-btn">
                  <Eye className="h-3 w-3 mr-1" /> Review
                </Button>
              </div>
            </div>
            {/* Inline preview of top duplicates */}
            <div className="mt-2 space-y-1">
              {duplicates.slice(0, 2).map((dup, i) => {
                const cfg = matchTypeConfig[dup.match_type] || matchTypeConfig.near;
                return (
                  <div key={i} className="flex items-center gap-2 p-1.5 rounded bg-white/60 dark:bg-slate-900/40 text-xs" data-testid={`dup-preview-${i}`}>
                    <span>{cfg.icon}</span>
                    <span className="font-mono font-medium text-primary">{dup.invoice_a.custom_id}</span>
                    <span className="text-muted-foreground">vs</span>
                    <span className="font-mono font-medium text-primary">{dup.invoice_b.custom_id}</span>
                    <span className="text-muted-foreground hidden sm:inline">{dup.invoice_a.academy_name}</span>
                    <span className="font-semibold tabular-nums">₹{dup.risk_amount.toLocaleString()}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border ${cfg.color}`}>{cfg.label} {dup.confidence}%</span>
                    <div className="flex gap-0.5 ml-auto">
                      <Button size="sm" variant="ghost" className="h-5 px-1.5 text-[10px]" onClick={() => handleResolveDuplicate(dup, 'merge')}><Merge className="h-2.5 w-2.5" /></Button>
                      <Button size="sm" variant="ghost" className="h-5 px-1.5 text-[10px] text-destructive" onClick={() => handleResolveDuplicate(dup, 'delete')}><Trash2 className="h-2.5 w-2.5" /></Button>
                      <Button size="sm" variant="ghost" className="h-5 px-1.5 text-[10px]" onClick={() => handleResolveDuplicate(dup, 'review')}><CheckCircle className="h-2.5 w-2.5" /></Button>
                    </div>
                  </div>
                );
              })}
              {duplicates.length > 2 && <p className="text-[10px] text-muted-foreground pl-1">+{duplicates.length - 2} more — Review tab for full report</p>}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Main Content: Table + AI Sidebar */}
      <div className="grid lg:grid-cols-4 gap-4">
        {/* Left: Invoices Table (3/4) */}
        <div className="lg:col-span-3 space-y-4">
          {/* Bulk Action Toolbar */}
          {selectedIds.length > 0 && (
            <div className="flex items-center gap-2 p-2 rounded-lg bg-primary/5 border border-primary/20" data-testid="bulk-toolbar">
              <Badge variant="secondary" className="tabular-nums">{selectedIds.length} selected</Badge>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={handleBulkRemind} disabled={reminding} data-testid="bulk-remind-btn">
                {reminding ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Send className="h-3 w-3 mr-1" />} Send Reminders
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={handleBulkMarkPaid} data-testid="bulk-paid-btn">
                <CheckCircle className="h-3 w-3 mr-1" /> Mark Paid
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setSelectedIds([])}>
                <XCircle className="h-3 w-3 mr-1" /> Clear
              </Button>
            </div>
          )}

          {/* Action Row */}
          <div className="flex flex-wrap items-center gap-2">
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
              <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="create-invoice-btn"><Plus className="h-4 w-4 mr-1" /> Manual Invoice</Button></DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2"><Plus className="h-4 w-4" /> Create Manual Invoice</DialogTitle>
                  <DialogDescription className="text-xs">
                    Bills the academy directly from Out-Play. Notification dispatched via Fail-Safe Relay (WhatsApp + Email) on creation.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleGenerate} className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Academy *</Label>
                    <Select value={form.academy_id} onValueChange={(v) => setForm({...form, academy_id: v})}>
                      <SelectTrigger data-testid="manual-inv-academy"><SelectValue placeholder="Select academy" /></SelectTrigger>
                      <SelectContent>{academies.map(a=><SelectItem key={a.id} value={a.id}>{a.custom_id} - {a.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Plan Type {form.amount && parseFloat(form.amount) > 0 && <span className="text-[10px] text-amber-600">(overridden by amount)</span>}</Label>
                      <Select value={form.plan || 'auto'} onValueChange={(v) => setForm({...form, plan: v === 'auto' ? '' : v})} disabled={!!form.amount && parseFloat(form.amount) > 0}>
                        <SelectTrigger data-testid="manual-inv-plan" className={form.amount && parseFloat(form.amount) > 0 ? 'opacity-50' : ''}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="auto">Use academy&apos;s current</SelectItem>
                          <SelectItem value="pro">Pro</SelectItem>
                          <SelectItem value="elite">Elite</SelectItem>
                          <SelectItem value="custom">Custom (set amount below)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Custom Amount (₹)</Label>
                      <Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} placeholder="Auto from plan" data-testid="manual-inv-amount" />
                      <p className="text-[10px] text-muted-foreground">Setting an amount overrides plan-based pricing</p>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Due Date <span className="text-muted-foreground">(default: today + 3 days)</span></Label>
                    <Input type="date" value={form.due_date} onChange={(e) => setForm({...form, due_date: e.target.value})} data-testid="manual-inv-due-date" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Description</Label>
                    <Input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="e.g. Q1 Renewal — Vijayawada Blues FC" data-testid="manual-inv-description" />
                  </div>
                  <label className="flex items-center gap-2 text-xs cursor-pointer select-none px-1 py-1.5 rounded-md hover:bg-muted/40">
                    <input type="checkbox" checked={form.notify} onChange={(e) => setForm({...form, notify: e.target.checked})} className="rounded" data-testid="manual-inv-notify" />
                    <span>Send instant invoice notification (WhatsApp + Email via Fail-Safe Relay)</span>
                  </label>
                  <Button type="submit" className="w-full" disabled={generating || !form.academy_id} data-testid="generate-inv-submit">
                    {generating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…</> : <>Generate Invoice</>}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="invoices" data-testid="tab-invoices"><Receipt className="h-4 w-4 mr-1" /> Invoices</TabsTrigger>
              <TabsTrigger value="duplicates" data-testid="tab-duplicates" className="relative">
                <Shield className="h-4 w-4 mr-1" /> AI Duplicates
                {hasDuplicates && <span className="ml-1 inline-flex items-center justify-center h-4 min-w-[16px] px-1 text-[10px] font-bold rounded-full bg-red-500 text-white">{summary.duplicates_found}</span>}
              </TabsTrigger>
              <TabsTrigger value="recovery" data-testid="tab-recovery"><Shield className="h-4 w-4 mr-1" /> Recovery Audit</TabsTrigger>
            </TabsList>

            {/* Invoices Tab */}
            <TabsContent value="invoices">
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className="text-sm">{activeFilter === 'all' ? 'All Invoices' : activeFilter === 'pending' ? 'Pending Invoices' : activeFilter === 'paid' ? 'Paid Invoices' : 'Overdue Invoices'} <Badge variant="secondary" className="ml-1 tabular-nums">{filtered.length}</Badge></CardTitle>
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Phase 68 — Academy filter */}
                      <Select value={academyFilter} onValueChange={setAcademyFilter}>
                        <SelectTrigger className="h-8 text-xs w-48" data-testid="inv-academy-filter">
                          <SelectValue placeholder="All Academies" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Academies</SelectItem>
                          {academies.map(a => (
                            <SelectItem key={a.id} value={a.id}>
                              {a.custom_id ? `${a.custom_id} — ` : ''}{a.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="relative w-56"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-8 text-xs" data-testid="inv-search" /></div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10"><Checkbox checked={selectedIds.length > 0 && selectedIds.length === filtered.filter(i => i.status !== 'cancelled' && i.status !== 'paid').length} onCheckedChange={toggleSelectAll} /></TableHead>
                          <TableHead className="w-32">Invoice</TableHead>
                          <TableHead>Academy</TableHead>
                          <TableHead className="hidden md:table-cell">Plan</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead className="hidden md:table-cell">Due</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="w-10"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filtered.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} className="text-center py-16">
                              <div className="flex flex-col items-center gap-2">
                                {activeFilter === 'overdue' ? <AlertTriangle className="h-10 w-10 text-muted-foreground/40" /> : activeFilter === 'paid' ? <CheckCircle className="h-10 w-10 text-muted-foreground/40" /> : <Receipt className="h-10 w-10 text-muted-foreground/40" />}
                                <p className="text-sm font-medium text-muted-foreground">No {activeFilter !== 'all' ? activeFilter : ''} invoices</p>
                              </div>
                            </TableCell>
                          </TableRow>
                        ) : filtered.map(inv => {
                          const dupInfo = duplicateLookup[inv.id];
                          return (
                            <TableRow key={inv.id} className={`group ${inv.status === 'cancelled' ? 'opacity-40' : ''}`} data-testid={`inv-row-${inv.custom_id}`}>
                              <TableCell>
                                {inv.status !== 'cancelled' && inv.status !== 'paid' && (
                                  <Checkbox checked={selectedIds.includes(inv.id)} onCheckedChange={() => toggleSelect(inv.id)} />
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1.5">
                                  {dupInfo && <span title={`${dupInfo.type} duplicate (${dupInfo.conf}%)`}>{dupInfo.icon}</span>}
                                  <span className="text-xs font-mono font-medium text-primary">{inv.custom_id}</span>
                                </div>
                                {inv.duplicate_status === 'resolved' && <Badge variant="outline" className="mt-0.5 text-[9px] px-1 py-0">resolved</Badge>}
                              </TableCell>
                              <TableCell>
                                <div><p className="font-medium text-sm">{inv.academy_name}</p><p className="text-xs text-muted-foreground font-mono">{inv.academy_custom_id}</p></div>
                              </TableCell>
                              <TableCell className="hidden md:table-cell"><Badge variant="outline" className="text-xs capitalize">{inv.plan}</Badge></TableCell>
                              <TableCell className="font-semibold tabular-nums">₹{(inv.amount||0).toLocaleString()}</TableCell>
                              <TableCell className="hidden md:table-cell text-sm">{inv.due_date}</TableCell>
                              <TableCell><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[inv.status]||'bg-slate-100'}`}>{inv.status}</span></TableCell>
                              <TableCell>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 opacity-60 group-hover:opacity-100" data-testid={`inv-actions-${inv.custom_id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-48">
                                    <DropdownMenuItem onClick={() => handleViewInvoice(inv)} data-testid="action-view"><Eye className="h-4 w-4 mr-2" /> View GST Invoice</DropdownMenuItem>
                                    {/* Phase 68 — Lifecycle reminder calls the real backend */}
                                    {inv.status !== 'paid' && inv.status !== 'cancelled' && (
                                      <DropdownMenuItem onClick={() => handleSendReminder(inv)} data-testid="action-remind">
                                        <MessageSquare className="h-4 w-4 mr-2" /> Send Reminder (WA + Email)
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem onClick={() => handleDownloadInvoice(inv)} disabled={downloadingIds.has(inv.id)} data-testid="action-download">
                                      {downloadingIds.has(inv.id) ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                                      {downloadingIds.has(inv.id) ? 'Generating...' : 'Download Tax Invoice'}
                                    </DropdownMenuItem>
                                    {/* Phase 68 — Digital receipt (paid invoices only) */}
                                    {inv.status === 'paid' && (
                                      <DropdownMenuItem onClick={() => handleDownloadReceipt(inv)} disabled={downloadingIds.has(inv.id)} data-testid="action-download-receipt">
                                        {downloadingIds.has(inv.id) ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                                        {downloadingIds.has(inv.id) ? 'Generating...' : 'Download Receipt'}
                                      </DropdownMenuItem>
                                    )}
                                    {inv.status !== 'paid' && inv.status !== 'cancelled' && (
                                      <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={() => { setShowMarkPaid(inv); setPayForm({...payForm, amount: String(inv.amount||'')}); }} data-testid="action-pay"><CreditCard className="h-4 w-4 mr-2" /> Mark as Paid</DropdownMenuItem>
                                        {inv.razorpay_order_id && (
                                          <DropdownMenuItem
                                            onClick={() => handleReconcileRazorpay(inv)}
                                            disabled={reconcilingIds.has(inv.id)}
                                            data-testid={`action-reconcile-razorpay-${inv.custom_id}`}
                                          >
                                            {reconcilingIds.has(inv.id)
                                              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                              : <RefreshCw className="h-4 w-4 mr-2 text-indigo-600" />}
                                            {reconcilingIds.has(inv.id) ? 'Checking Razorpay…' : 'Reconcile with Razorpay'}
                                          </DropdownMenuItem>
                                        )}
                                        <DropdownMenuItem onClick={() => { setShowEdit(inv); setEditForm({ amount: String(inv.amount||''), due_date: inv.due_date || '', notes: inv.notes || '' }); }} data-testid="action-edit"><Edit className="h-4 w-4 mr-2" /> Edit Invoice</DropdownMenuItem>
                                      </>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleDelete(inv)} className="text-destructive" data-testid="action-delete"><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* AI Duplicates Tab */}
            <TabsContent value="duplicates">
              <div className="space-y-4">
                {summary && (
                  <Card data-testid="dup-report">
                    <CardContent className="p-5">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="space-y-3">
                          <div className="flex items-center gap-2"><Shield className="h-5 w-5 text-primary" /><h3 className="font-display font-semibold">AI Duplicate Analysis</h3></div>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                            <div><p className="text-xs text-muted-foreground">Scanned</p><p className="text-xl font-display font-bold tabular-nums">{summary.total_scanned}</p></div>
                            <div><p className="text-xs text-muted-foreground">Duplicates</p><p className={`text-xl font-display font-bold tabular-nums ${summary.duplicates_found > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{summary.duplicates_found}</p></div>
                            <div><p className="text-xs text-muted-foreground">Risk</p><p className={`text-xl font-display font-bold tabular-nums ${summary.total_risk > 0 ? 'text-red-600' : 'text-emerald-600'}`}>₹{summary.total_risk.toLocaleString()}</p></div>
                            <div><p className="text-xs text-muted-foreground">Accuracy</p><p className="text-xl font-display font-bold tabular-nums">{summary.accuracy}%</p></div>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <Button size="sm" onClick={() => handleScanDuplicates(false)} disabled={scanning}>{scanning ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Scan className="h-4 w-4 mr-1" />} Rescan</Button>
                          {hasDuplicates && <Button size="sm" variant="destructive" onClick={handleCleanAll} disabled={cleaning}>{cleaning ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />} Auto-Clean</Button>}
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-border flex items-center gap-4 text-sm">
                        <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-red-500" /> Exact: {summary.exact_matches}</span>
                        <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-amber-500" /> Fuzzy: {summary.fuzzy_matches}</span>
                        <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-yellow-500" /> Near: {summary.near_matches}</span>
                        <span className="text-xs text-muted-foreground ml-auto">{summary.processing_time_seconds}s</span>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {duplicates.length === 0 ? (
                  <Card className="border-dashed"><CardContent className="flex flex-col items-center justify-center py-16">
                    <Shield className="h-12 w-12 text-emerald-500/40 mb-4" /><h3 className="text-lg font-semibold text-emerald-600 mb-1">All Clear</h3>
                    <p className="text-sm text-muted-foreground">No duplicates. {summary?.total_scanned || 0} invoices scanned.</p>
                  </CardContent></Card>
                ) : (
                  <Card><CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2">Duplicate Pairs <Badge variant="destructive" className="text-xs">{duplicates.length}</Badge></CardTitle></CardHeader>
                    <CardContent className="p-0"><Table><TableHeader><TableRow>
                      <TableHead className="w-8">#</TableHead><TableHead>Invoice A</TableHead><TableHead className="w-6"></TableHead><TableHead>Invoice B</TableHead><TableHead>Type</TableHead><TableHead>Score</TableHead><TableHead>Risk</TableHead><TableHead className="w-32">Actions</TableHead>
                    </TableRow></TableHeader><TableBody>
                      {duplicates.map((dup, i) => {
                        const cfg = matchTypeConfig[dup.match_type] || matchTypeConfig.near;
                        return (<TableRow key={i} data-testid={`dup-pair-${i}`}>
                          <TableCell className="text-xs text-muted-foreground">{i+1}</TableCell>
                          <TableCell><p className="font-mono text-xs font-medium text-primary">{dup.invoice_a.custom_id}</p><p className="text-[10px] text-muted-foreground">{dup.invoice_a.academy_name} • {dup.invoice_a.status}</p></TableCell>
                          <TableCell><ArrowRight className="h-3 w-3 text-muted-foreground" /></TableCell>
                          <TableCell><p className="font-mono text-xs font-medium text-primary">{dup.invoice_b.custom_id}</p><p className="text-[10px] text-muted-foreground">{dup.invoice_b.academy_name} • {dup.invoice_b.status}</p></TableCell>
                          <TableCell><span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${cfg.color}`}>{cfg.icon} {cfg.label}</span></TableCell>
                          <TableCell><div className="space-y-0.5"><span className="text-xs font-semibold tabular-nums">{dup.confidence}%</span><Progress value={dup.confidence} className="h-1 w-14" /></div></TableCell>
                          <TableCell className="font-semibold text-red-600 tabular-nums text-xs">₹{dup.risk_amount.toLocaleString()}</TableCell>
                          <TableCell><div className="flex gap-0.5">
                            <Button size="sm" variant="outline" className="h-6 text-[10px] px-1.5" onClick={() => handleResolveDuplicate(dup, 'merge')}><Merge className="h-2.5 w-2.5 mr-0.5" />Merge</Button>
                            <Button size="sm" variant="outline" className="h-6 text-[10px] px-1.5 text-destructive" onClick={() => handleResolveDuplicate(dup, 'delete')}><Trash2 className="h-2.5 w-2.5" /></Button>
                            <Button size="sm" variant="outline" className="h-6 text-[10px] px-1.5" onClick={() => handleResolveDuplicate(dup, 'review')}><CheckCircle className="h-2.5 w-2.5" /></Button>
                          </div></TableCell>
                        </TableRow>);
                      })}
                    </TableBody></Table></CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            {/* Recovery Audit Tab — Phase 254 */}
            <TabsContent value="recovery">
              <div className="space-y-4">
                <Card data-testid="recovery-audit-panel">
                  <CardHeader className="pb-3">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div>
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Shield className="h-4 w-4 text-emerald-600" /> Payment Recovery Audit Ledger
                        </CardTitle>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          Last 30 days of subscription invoices self-healed via the Razorpay pull-API — manual operator clicks + the 02:00 IST nightly cron.
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={handleRunSweepNow}
                        disabled={sweepRunning}
                        className="bg-slate-900 hover:bg-slate-800 text-white"
                        data-testid="recovery-run-sweep"
                      >
                        {sweepRunning ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                        {sweepRunning ? 'Sweeping…' : 'Re-run Sweep Now'}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Summary ribbon */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                      <div className="bg-emerald-50/50 border border-emerald-200 p-3 rounded-xl">
                        <div className="text-[10px] uppercase font-bold text-emerald-700 tracking-wider">Recovered Capital</div>
                        <div className="text-xl font-black text-emerald-900 mt-1 tabular-nums" data-testid="recovery-total-amount">
                          ₹{(recoveryData?.summary?.total_recovered || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>
                      <div className="bg-slate-50 border border-slate-200 p-3 rounded-xl">
                        <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Incidents Resolved</div>
                        <div className="text-xl font-black text-slate-900 mt-1 tabular-nums" data-testid="recovery-incident-count">
                          {recoveryData?.summary?.incidents_resolved || 0}
                          {' '}
                          <span className="text-xs font-medium text-slate-400 ml-1.5">invoices</span>
                        </div>
                      </div>
                      <div className="bg-indigo-50/40 border border-indigo-200 p-3 rounded-xl text-xs">
                        <div className="flex justify-between items-center">
                          <span className="text-slate-600">Nightly cron:</span>
                          <span className="font-mono font-bold text-indigo-700" data-testid="recovery-cron-count">{recoveryData?.summary?.by_source?.nightly_cron || 0}</span>
                        </div>
                        <div className="flex justify-between items-center mt-1.5">
                          <span className="text-slate-600">Manual clicks:</span>
                          <span className="font-mono font-bold text-indigo-700" data-testid="recovery-manual-count">{recoveryData?.summary?.by_source?.manual_reconcile || 0}</span>
                        </div>
                      </div>
                    </div>

                    {/* Logs table */}
                    {recoveryLoading ? (
                      <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
                    ) : (recoveryData?.logs?.length || 0) === 0 ? (
                      <div className="py-12 text-center text-xs text-slate-400 border border-dashed border-slate-200 rounded-xl" data-testid="recovery-empty-state">
                        No reconciliation events in the last 30 days. (Either no stuck invoices needed self-healing — or your Razorpay webhook is firing perfectly, which is a great thing.)
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-[10px] uppercase tracking-wider w-32">Source</TableHead>
                            <TableHead className="text-[10px] uppercase tracking-wider">Invoice</TableHead>
                            <TableHead className="text-[10px] uppercase tracking-wider">Operator</TableHead>
                            <TableHead className="text-[10px] uppercase tracking-wider">When (UTC)</TableHead>
                            <TableHead className="text-[10px] uppercase tracking-wider text-right">Recovered</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {recoveryData.logs.map((log) => (
                            <TableRow key={log.id} data-testid={`recovery-row-${log.custom_id}`}>
                              <TableCell>
                                {log.source === 'nightly_cron' ? (
                                  <Badge variant="outline" className="bg-indigo-50 border-indigo-300 text-indigo-800 text-[10px]">🌙 Nightly</Badge>
                                ) : (
                                  <Badge variant="outline" className="bg-amber-50 border-amber-300 text-amber-800 text-[10px]">⚡ Manual</Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="font-mono text-xs font-semibold text-primary">{log.custom_id}</div>
                                <div className="text-[10px] text-muted-foreground truncate max-w-[160px]">{log.academy_name}</div>
                              </TableCell>
                              <TableCell className="text-[11px] text-slate-600 font-mono truncate max-w-[160px]">{log.operator}</TableCell>
                              <TableCell className="text-[10px] text-slate-500 font-mono">
                                {log.reconciled_at ? new Date(log.reconciled_at).toISOString().replace('T', ' ').slice(0, 16) : '—'}
                              </TableCell>
                              <TableCell className="text-right font-bold tabular-nums text-emerald-700">₹{log.amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Right: AI Insights Panel (1/4) */}
        <div className="space-y-4">
          <Card data-testid="ai-insights-panel">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-primary" /> AI Recommendations
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {recs.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-6">No recommendations</p>
              ) : recs.map((rec, i) => (
                <div key={rec.id} className="p-3 rounded-lg bg-muted/50 border border-border space-y-2" data-testid={`ai-rec-${rec.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-1.5">
                      {rec.priority === 'high' && <span className="h-2 w-2 rounded-full bg-red-500 flex-shrink-0" />}
                      {rec.priority === 'medium' && <span className="h-2 w-2 rounded-full bg-amber-500 flex-shrink-0" />}
                      {rec.priority === 'info' && <span className="h-2 w-2 rounded-full bg-blue-500 flex-shrink-0" />}
                      <p className="text-xs font-semibold">{rec.title}</p>
                    </div>
                    {rec.confidence && <Badge variant="outline" className="text-[9px] px-1">{rec.confidence}%</Badge>}
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">{rec.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-primary tabular-nums">{rec.impact}</span>
                    {rec.action === 'auto_remind' && (
                      <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={handleBulkRemind} disabled={reminding}>
                        {reminding ? <Loader2 className="h-2.5 w-2.5 mr-0.5 animate-spin" /> : <Send className="h-2.5 w-2.5 mr-0.5" />} Auto-Send
                      </Button>
                    )}
                    {rec.action === 'clean_duplicates' && (
                      <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={handleCleanAll} disabled={cleaning}>
                        {cleaning ? <Loader2 className="h-2.5 w-2.5 mr-0.5 animate-spin" /> : <Zap className="h-2.5 w-2.5 mr-0.5" />} Clean Now
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Mini Revenue Stats */}
          {insights?.stats && (
            <Card data-testid="revenue-mini">
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="h-4 w-4 text-emerald-600" /> Revenue</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between text-xs"><span className="text-muted-foreground">Collected</span><span className="font-semibold tabular-nums text-emerald-600">₹{insights.stats.paid_amount.toLocaleString()}</span></div>
                <div className="flex justify-between text-xs"><span className="text-muted-foreground">Pending</span><span className="font-semibold tabular-nums text-amber-600">₹{insights.stats.pending_amount.toLocaleString()}</span></div>
                {insights.stats.duplicate_risk > 0 && <div className="flex justify-between text-xs"><span className="text-muted-foreground">Dup. Risk</span><span className="font-semibold tabular-nums text-red-600">-₹{insights.stats.duplicate_risk.toLocaleString()}</span></div>}
                <div className="pt-2 border-t border-border flex justify-between text-xs"><span className="text-muted-foreground">Forecast</span><span className="font-semibold tabular-nums">₹{insights.stats.forecast_next_month.toLocaleString()}</span></div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Mark as Paid Dialog */}
      <Dialog open={!!showMarkPaid} onOpenChange={() => setShowMarkPaid(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CreditCard className="h-5 w-5 text-primary" /> Mark Invoice Paid</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="p-3 rounded-lg bg-muted/50"><p className="font-medium text-sm">{showMarkPaid?.custom_id}</p><p className="text-xs text-muted-foreground">{showMarkPaid?.academy_name} — ₹{(showMarkPaid?.amount||0).toLocaleString()}</p></div>
            <div className="space-y-1.5"><Label className="text-xs">Amount Paid (₹) *</Label><Input type="number" value={payForm.amount} onChange={(e) => setPayForm({...payForm, amount: e.target.value})} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Payment Date *</Label><Input type="date" value={payForm.payment_date} onChange={(e) => setPayForm({...payForm, payment_date: e.target.value})} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Mode *</Label>
              <Select value={payForm.payment_mode} onValueChange={(v) => setPayForm({...payForm, payment_mode: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="UPI">UPI</SelectItem><SelectItem value="Bank Transfer">Bank Transfer</SelectItem><SelectItem value="Card">Card</SelectItem><SelectItem value="Cash">Cash</SelectItem></SelectContent></Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Reference ID *</Label><Input value={payForm.reference_id} onChange={(e) => setPayForm({...payForm, reference_id: e.target.value})} placeholder="TXN123456" /></div>
            <Button className="w-full" onClick={handleMarkPaid} data-testid="confirm-pay"><CheckCircle className="h-4 w-4 mr-2" /> Confirm Payment</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Invoice Dialog */}
      <Dialog open={!!showEdit} onOpenChange={() => setShowEdit(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Edit className="h-5 w-5 text-primary" /> Edit Invoice</DialogTitle></DialogHeader>
          {showEdit && (
            <div className="space-y-3">
              <div className="p-3 rounded-lg bg-muted/50"><p className="font-medium text-sm">{showEdit.custom_id}</p><p className="text-xs text-muted-foreground">{showEdit.academy_name}</p></div>
              <div className="space-y-1.5"><Label className="text-xs">Amount (₹)</Label><Input type="number" value={editForm.amount} onChange={(e) => setEditForm({...editForm, amount: e.target.value})} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Due Date</Label><Input type="date" value={editForm.due_date} onChange={(e) => setEditForm({...editForm, due_date: e.target.value})} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Notes</Label><Input value={editForm.notes} onChange={(e) => setEditForm({...editForm, notes: e.target.value})} /></div>
              <Button className="w-full" onClick={handleEdit} data-testid="save-edit"><CheckCircle className="h-4 w-4 mr-2" /> Save Changes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
